import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { SignatureCard } from '@common/SignatureCard';
import { useProjectId } from '@config/config.hooks';
import { AxiosErrorResponse } from '@core/error.models';
import { FormTextareaWithMaxLength } from '@core/FormTextareaWithMaxLength';
import { Note } from '@core/Note/Note';
import {
  isDesignModificationStatusInitial,
  IT3TopBtnsVisibility,
  useIsAttachmentUploadLocked,
  useIsDesignerAttachmentUploadEnabled,
  useIsIceCertificateUploaded,
  useIsIceRequired,
  useIsShowReleasedConstructionInfo,
} from '@designModification/designModification.hooks';
import {
  EDesignModificationFormKeys,
  EDesignModificationConfirmationActionType,
  EDesignModificationStatus,
  IApproveProjectDesignModificationRequest,
  IProjectDesignModification,
} from '@designModification/designModification.models';
import {
  IApproveProjectDesignModificationRequestProps,
  ICreateProjectDesignModificationRequestProps,
} from '@designModification/designModification.queries';
import { Attachments } from '@file/Attachments';
import { DownloadButton } from '@file/DownloadButton';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useFetchAttachmentDesignModification } from '@file/projectFile/projectFile.queries';
import { downloadAttachmentsAsync } from '@file/projectFile/projectFile.services';
import {
  Card,
  ComponentType,
  FormFieldMethods,
  Grid,
  Icon,
  IItemResponse,
  LoadingButton,
  ToastMessage,
  toastMessage,
  Tooltip,
} from '@gcl/react-core';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import { UseMutationResult } from '@tanstack/react-query';
import React from 'react';

interface IDesignModificationDetailTabSignatureProps {
  formFieldMethods: FormFieldMethods;
  mode: DetailPageAccessMode;
  designModificationId?: number;
  designModification?: IProjectDesignModification;
  masterSchedule?: IMasterScheduleDetail;
  isReadOnly: boolean;
  onClickApprove: (confirmType: EDesignModificationConfirmationActionType, e: any) => void;
  mutationApprove: UseMutationResult<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IApproveProjectDesignModificationRequestProps
  >;
  mutationCreate: UseMutationResult<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    ICreateProjectDesignModificationRequestProps
  >;
  mutationApproveAndCheckedAsLambeth: UseMutationResult<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IApproveProjectDesignModificationRequest
  >;

  topBtnsVisibility: IT3TopBtnsVisibility;
  confirmationActionType?: EDesignModificationConfirmationActionType;
  isSuperseded: boolean;
  isAnyActionLoading: boolean;
}
export const DesignModificationDetailTabSignature = ({
  designModification,
  formFieldMethods,
  mode,
  designModificationId,
  isReadOnly,
  onClickApprove,
  mutationApprove,
  mutationCreate,
  mutationApproveAndCheckedAsLambeth,
  topBtnsVisibility,
  confirmationActionType,
  masterSchedule,
  isSuperseded,
  isAnyActionLoading,
}: IDesignModificationDetailTabSignatureProps) => {
  const projectId = useProjectId();
  const formDataRiskCategory = formFieldMethods.watch(EDesignModificationFormKeys.RISK_CATEGORY)?.category as
    | ERiskCategory
    | undefined;
  const {
    isShowIssueToTwcAsSrpBtn,
    isShowIssueToTwcAsDesignerBtn,
    isShowDesignerApproveBtn,
    isShowReviewerApproveBtn,
    isShowIceApproveBtn,
    isShowIssueToDesignerBtn,
    isShowRequestEndorsementBtn,
    isShowDesignAgreedAndCheckedByLambethBtn,
  } = topBtnsVisibility;

  const isShowReleasedConstructionInfo = useIsShowReleasedConstructionInfo(
    designModificationId ?? 0,
    projectId,
    designModification,
    formDataRiskCategory,
  );

  const isAttachmentUploadLocked = useIsAttachmentUploadLocked(masterSchedule);
  const isDesignerAttachmentUploadEnabled = useIsDesignerAttachmentUploadEnabled(isSuperseded, designModification);
  const isIceCertificateUploaded = useIsIceCertificateUploaded(designModificationId ?? 0, projectId);
  const isIceRequired = useIsIceRequired(designModification);

  const [designerAttachmentCount, setDesignerAttachmentCount] = React.useState(0);
  const [designerSupportingCount, setDesignerSupportingCount] = React.useState(0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SignatureCard
          className="gcl-gems-t3-signature-card-body"
          title="Initiated by SRP/TWD"
          signProfile={designModification?.siteEngineerProjectUserProfile?.userProfile?.signatureUserProfile}
          signUser={designModification?.siteEngineerProjectUserProfile?.userProfile?.displayName}
          signDate={designModification?.siteEngineerDate}
          onRenderContent={(signatureComponent) => {
            return (
              <>
                {signatureComponent}
                {!designModification?.siteEngineerProjectUserProfile && isShowIssueToTwcAsSrpBtn && (
                  <LoadingButton
                    className="gcl-mb-xs"
                    disabled={isAnyActionLoading}
                    prefixIconName="EnvelopeCheck"
                    type={ComponentType.Success}
                    isLoading={
                      (mutationApprove.isLoading || mutationCreate.isLoading) &&
                      confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsSrp
                    }
                    isSuccess={
                      (mutationApprove.isSuccess || mutationCreate.isSuccess) &&
                      confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsSrp
                    }
                    isError={
                      (mutationApprove.isError || mutationCreate.isError) &&
                      confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsSrp
                    }
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                      onClickApprove(EDesignModificationConfirmationActionType.issueToTwcAsSrp, e)
                    }
                    timeToResetSuccessState={3000}
                    timeToResetErrorState={5000}
                  >
                    Issue to TWC as SRP
                  </LoadingButton>
                )}
                {!designModification?.siteEngineerProjectUserProfile && isShowIssueToTwcAsDesignerBtn && (
                  <LoadingButton
                    disabled={isAnyActionLoading}
                    prefixIconName="EnvelopeCheck"
                    type={ComponentType.Success}
                    isLoading={
                      (mutationApprove.isLoading || mutationCreate.isLoading) &&
                      confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsDesigner
                    }
                    isSuccess={
                      (mutationApprove.isSuccess || mutationCreate.isSuccess) &&
                      confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsDesigner
                    }
                    isError={
                      (mutationApprove.isError || mutationCreate.isError) &&
                      confirmationActionType === EDesignModificationConfirmationActionType.issueToTwcAsDesigner
                    }
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                      onClickApprove(EDesignModificationConfirmationActionType.issueToTwcAsDesigner, e)
                    }
                    timeToResetSuccessState={3000}
                    timeToResetErrorState={5000}
                  >
                    Issue to TWC as Designer
                  </LoadingButton>
                )}
              </>
            );
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SignatureCard
          className="gcl-gems-t3-signature-card-body"
          title="Issued by TWC"
          signProfile={designModification?.twcprojectUserProfile?.userProfile?.signatureUserProfile}
          signUser={designModification?.twcprojectUserProfile?.userProfile?.displayName}
          signDate={designModification?.twcDate}
          onRenderContent={(signatureComponent) => {
            if (
              !isDesignModificationStatusInitial(designModification?.status) &&
              designModification?.isDesignerAdministratorInitiate
            ) {
              return (
                <Note className="gcl-mt-xs" componentType={ComponentType.Primary}>
                  T3 form is initiated by design team. TWC does not need to sign
                </Note>
              );
            }
            return (
              <>
                {signatureComponent}
                <div>
                  {!designModification?.twcprojectUserProfile && isShowIssueToDesignerBtn && (
                    <LoadingButton
                      disabled={isAnyActionLoading}
                      prefixIconName="EnvelopeCheck"
                      type={ComponentType.Success}
                      isLoading={
                        (mutationApprove.isLoading || mutationCreate.isLoading) &&
                        confirmationActionType === EDesignModificationConfirmationActionType.issueToDesigner
                      }
                      isSuccess={
                        (mutationApprove.isSuccess || mutationCreate.isSuccess) &&
                        confirmationActionType === EDesignModificationConfirmationActionType.issueToDesigner
                      }
                      isError={
                        (mutationApprove.isError || mutationCreate.isError) &&
                        confirmationActionType === EDesignModificationConfirmationActionType.issueToDesigner
                      }
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                        onClickApprove(EDesignModificationConfirmationActionType.issueToDesigner, e)
                      }
                      timeToResetSuccessState={3000}
                      timeToResetErrorState={5000}
                    >
                      Issue to Designer as TWC
                    </LoadingButton>
                  )}
                </div>
              </>
            );
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SignatureCard
          className="gcl-gems-t3-signature-card-body"
          title="Change Agreed by TWD"
          signProfile={designModification?.designerProjectUserProfile?.userProfile?.signatureUserProfile}
          signUser={designModification?.designerProjectUserProfile?.userProfile?.displayName}
          signDate={designModification?.designerDate}
          onRenderContent={(signatureComponent) => {
            const isReferToDesignerTransmittal =
              designModification?.status === EDesignModificationStatus.referToDesignerTransmittal; // to-do: also check linked to designer transmittal

            const commonForm = (
              <>
                <LabelWithMessage
                  labelFor={EDesignModificationFormKeys.DESIGNER_COMMENT}
                  text="TWD's Remarks"
                  className="gcl-mt-sm"
                />
                <FormTextareaWithMaxLength
                  formFieldMethods={formFieldMethods}
                  id={EDesignModificationFormKeys.DESIGNER_COMMENT}
                  max={800}
                  disabled={!isDesignerAttachmentUploadEnabled}
                />

                {!isReferToDesignerTransmittal && (
                  <>
                    <LabelWithMessage
                      labelFor={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS}
                      text="Designer Attachment (Completed Modification)"
                      className="gcl-mt-sm"
                      message="PDF files only for any upload items"
                    />

                    <Attachments
                      setFileCount={setDesignerAttachmentCount}
                      className="gcl-my-sm gcl-gems-t3-signature-card-body"
                      mode={isAttachmentUploadLocked || !isDesignerAttachmentUploadEnabled ? 'View' : mode}
                      id={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS}
                      fileType={EProjectFileType.T3_DESIGNER}
                      uploadFileRequestParam={{ designModificationId }}
                      useFetchFiles={useFetchAttachmentDesignModification}
                      useFetchFilesProps={{
                        projectId,
                        designModificationId: designModificationId ?? 0,
                        fileType: EProjectFileType.T3_DESIGNER,
                      }}
                      customHeaderButtons={[
                        <Tooltip
                          overlay="Download all files as zip"
                          id="download-designer-attachment"
                          key="download-designer-attachment"
                        >
                          {designerAttachmentCount > 0 ? (
                            <DownloadButton
                              exportFunction={downloadAttachmentsAsync}
                              defaultExportProps={{
                                projectId,
                                designModificationId,
                                fileType: EProjectFileType.T3_DESIGNER,
                              }}
                              buttonRender={(exportFile) => (
                                <Icon
                                  onClick={() => exportFile()}
                                  iconName="FileZip"
                                  className="gcl-fc-primary-400 gcl-ic-btn-lg"
                                />
                              )}
                              onError={(errors) => {
                                toastMessage(
                                  <ToastMessage
                                    message={errors.errorMessage ?? 'Failed to download file'}
                                    toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                                  />,
                                );
                              }}
                            />
                          ) : (
                            <></>
                          )}
                        </Tooltip>,
                      ]}
                      useFetchFilesQueryKey={[
                        projectId,
                        designModificationId,
                        FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_MODIFICATIONS,
                        EProjectFileType.T3_DESIGNER,
                      ]}
                      acceptFileType="application/pdf"
                      formFieldMethods={formFieldMethods}
                      formKeyToUpdateInUpdateMode={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS}
                    />
                  </>
                )}
                {!isReferToDesignerTransmittal && (
                  <>
                    <LabelWithMessage
                      labelFor={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION}
                      text="Designer Supporting - Calculation / Reports (Completed Modification)"
                      message="PDF files only for any upload items"
                    />
                    <Attachments
                      setFileCount={setDesignerSupportingCount}
                      className="gcl-my-xs gcl-gems-t3-signature-card-body"
                      mode={isAttachmentUploadLocked || !isDesignerAttachmentUploadEnabled ? 'View' : mode}
                      id={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION}
                      fileType={EProjectFileType.T3_DESIGNER_CALCULATION}
                      uploadFileRequestParam={{ designModificationId }}
                      useFetchFiles={useFetchAttachmentDesignModification}
                      useFetchFilesProps={{
                        projectId,
                        designModificationId: designModificationId ?? 0,
                        fileType: EProjectFileType.T3_DESIGNER_CALCULATION,
                      }}
                      customHeaderButtons={[
                        <Tooltip
                          overlay="Download all files as zip"
                          id="download-designer-supporting"
                          key="download-designer-supporting"
                        >
                          {designerSupportingCount > 0 ? (
                            <DownloadButton
                              exportFunction={downloadAttachmentsAsync}
                              defaultExportProps={{
                                projectId,
                                designModificationId,
                                fileType: EProjectFileType.T3_DESIGNER_CALCULATION,
                              }}
                              buttonRender={(exportFile) => (
                                <Icon
                                  onClick={() => exportFile()}
                                  iconName="FileZip"
                                  className="gcl-fc-primary-400 gcl-ic-btn-lg"
                                />
                              )}
                              onError={(errors) => {
                                toastMessage(
                                  <ToastMessage
                                    message={errors.errorMessage ?? 'Failed to download file'}
                                    toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                                  />,
                                );
                              }}
                            />
                          ) : (
                            <></>
                          )}
                        </Tooltip>,
                      ]}
                      useFetchFilesQueryKey={[
                        projectId,
                        designModificationId,
                        FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_MODIFICATIONS,
                        EProjectFileType.T3_DESIGNER_CALCULATION,
                      ]}
                      acceptFileType="application/pdf"
                      formFieldMethods={formFieldMethods}
                      formKeyToUpdateInUpdateMode={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION}
                    />
                  </>
                )}
              </>
            );
            if (
              !signatureComponent &&
              !isShowDesignerApproveBtn &&
              !isShowDesignAgreedAndCheckedByLambethBtn &&
              !isShowDesignerApproveBtn
            )
              return <div>{commonForm}</div>;

            const confirmNote = (
              <>
                Please confirm you wish to approve this T3 and T3 is{' '}
                <span className="gcl-gems-design-modification-detail-confirmation-danger-text">checked</span>
                <br />
                Note:
                <ol>
                  <li>
                    The &apos;Proposed Modification Attachment&apos; (if any) uploaded by the site will not be approved
                    or released for construction.
                  </li>
                  <li>
                    TWD must upload a Designer Attachment (Completed Modification). This sketch will be released for
                    construction along with the T3.
                  </li>
                  <li>T3 soft chop will be applied to the uploaded Designer Attachment.</li>
                  <li>
                    <span className="gcl-gems-design-modification-detail-confirmation-danger-text">
                      PDF markup may be lost
                    </span>
                    , markup should be flattened by printing to a new PDF file.
                  </li>
                  <li>
                    Change Agreed by TWD and Change Agreed by Checker are signed simultaneously and T3 can be release
                    for construction directly by TWC.
                  </li>
                </ol>
              </>
            );

            return (
              <>
                {signatureComponent}
                {!designModification?.designerProjectUserProfile && confirmNote}
                <Grid container>
                  {!designModification?.designerProjectUserProfile && isShowRequestEndorsementBtn && (
                    <LoadingButton
                      disabled={isAnyActionLoading}
                      prefixIconName="EnvelopeCheck"
                      className="gcl-mr-sm"
                      type={ComponentType.Secondary}
                      isLoading={
                        mutationApprove.isLoading &&
                        confirmationActionType === EDesignModificationConfirmationActionType.requestEndorsement
                      }
                      isSuccess={
                        mutationApprove.isSuccess &&
                        confirmationActionType === EDesignModificationConfirmationActionType.requestEndorsement
                      }
                      isError={
                        mutationApprove.isError &&
                        confirmationActionType === EDesignModificationConfirmationActionType.requestEndorsement
                      }
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                        onClickApprove(EDesignModificationConfirmationActionType.requestEndorsement, e)
                      }
                      timeToResetSuccessState={3000}
                      timeToResetErrorState={5000}
                    >
                      Request Endorsement
                    </LoadingButton>
                  )}
                  {!designModification?.designerProjectUserProfile && isShowDesignerApproveBtn && (
                    <LoadingButton
                      disabled={isAnyActionLoading}
                      prefixIconName="Pen"
                      className="gcl-mr-sm"
                      type={ComponentType.Success}
                      isLoading={
                        mutationApprove.isLoading &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveDesigner
                      }
                      isSuccess={
                        mutationApprove.isSuccess &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveDesigner
                      }
                      isError={
                        mutationApprove.isError &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveDesigner
                      }
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                        onClickApprove(EDesignModificationConfirmationActionType.approveDesigner, e)
                      }
                      timeToResetSuccessState={3000}
                      timeToResetErrorState={5000}
                    >
                      Approve as Designer
                    </LoadingButton>
                  )}
                  {!designModification?.designerProjectUserProfile && isShowDesignAgreedAndCheckedByLambethBtn && (
                    <LoadingButton
                      disabled={isAnyActionLoading}
                      prefixIconName="Pen"
                      type={ComponentType.Success}
                      isLoading={
                        mutationApproveAndCheckedAsLambeth.isLoading &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth
                      }
                      isSuccess={
                        mutationApproveAndCheckedAsLambeth.isSuccess &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth
                      }
                      isError={
                        mutationApproveAndCheckedAsLambeth.isError &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth
                      }
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                        onClickApprove(EDesignModificationConfirmationActionType.approveAndCheckedAsLambeth, e)
                      }
                      timeToResetSuccessState={3000}
                      timeToResetErrorState={5000}
                    >
                      Design Agreed and Checked By Lambeth
                    </LoadingButton>
                  )}
                </Grid>

                {commonForm}
              </>
            );
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <SignatureCard
          className="gcl-gems-t3-signature-card-body"
          title="Change Agreed by Checker"
          signProfile={designModification?.iceprojectUserProfile?.userProfile?.signatureUserProfile}
          signUser={designModification?.iceprojectUserProfile?.userProfile?.displayName}
          signDate={designModification?.iceDate}
          onRenderContent={(signatureComponent) => {
            const commonForm = (
              <>
                <LabelWithMessage labelFor={EDesignModificationFormKeys.ICE_COMMENT} text="Checker's Remarks" />
                <FormTextareaWithMaxLength
                  formFieldMethods={formFieldMethods}
                  id={EDesignModificationFormKeys.ICE_COMMENT}
                  max={800}
                  disabled={!isShowIceApproveBtn}
                />
              </>
            );

            if (
              (designModification?.status === EDesignModificationStatus.completed ||
                designModification?.status === EDesignModificationStatus.referToDesignerTransmittal) &&
              designModification.riskCategory === ERiskCategory.D &&
              !isIceRequired
            ) {
              return (
                <Note className="gcl-my-sm" componentType={ComponentType.Primary}>
                  ICE sign is not required.
                </Note>
              );
            }
            if (!isIceRequired && designModification?.riskCategory === ERiskCategory.D) {
              return (
                <Note className="gcl-my-sm" componentType={ComponentType.Primary}>
                  ICE check is optional.
                </Note>
              );
            }
            if (isIceCertificateUploaded) {
              return (
                <Note className="gcl-my-sm" componentType={ComponentType.Primary}>
                  Manually Endorsed. Refer to scanned PDF
                </Note>
              );
            }
            return (
              <>
                {designModification?.status === EDesignModificationStatus.waitingToUploadSignedCert &&
                  !isSuperseded && (
                    <Note
                      className="gcl-my-sm"
                      componentType={ComponentType.Info}
                      prefixIcon={<Icon iconName="Lightbulb" />}
                    >
                      The TWC has issued this form to the checker and it is waiting for approval
                    </Note>
                  )}
                {designModification?.status === EDesignModificationStatus.waitingToTWCIssue && !isSuperseded && (
                  <Note
                    className="gcl-my-sm"
                    componentType={ComponentType.Warning}
                    prefixIcon={<Icon iconName="Lightbulb" />}
                  >
                    The TWC has not issued this form to the checker yet.
                  </Note>
                )}

                {isShowIceApproveBtn && !designModification?.iceprojectUserProfile && (
                  <LoadingButton
                    disabled={isAnyActionLoading}
                    prefixIconName="Pen"
                    type={ComponentType.Success}
                    isLoading={
                      mutationApprove.isLoading &&
                      confirmationActionType === EDesignModificationConfirmationActionType.approveChecker
                    }
                    isSuccess={
                      mutationApprove.isSuccess &&
                      confirmationActionType === EDesignModificationConfirmationActionType.approveChecker
                    }
                    isError={
                      mutationApprove.isError &&
                      confirmationActionType === EDesignModificationConfirmationActionType.approveChecker
                    }
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                      onClickApprove(EDesignModificationConfirmationActionType.approveChecker, e)
                    }
                    timeToResetSuccessState={3000}
                    timeToResetErrorState={5000}
                  >
                    Approve as ICE
                  </LoadingButton>
                )}
                {signatureComponent}
                {commonForm}
              </>
            );
          }}
        />
      </Grid>

      {!!masterSchedule?.responsibleReviewer?.id && (
        <Grid item xs={12} md={6}>
          <SignatureCard
            className="gcl-gems-t3-signature-card-body"
            title="Change Agreed by Reviewer"
            signProfile={designModification?.reviewerProjectUserProfile?.userProfile?.signatureUserProfile}
            signUser={designModification?.reviewerProjectUserProfile?.userProfile?.displayName}
            signDate={designModification?.reviewerDate}
            onRenderContent={(signatureComponent) => {
              const commonForm = (
                <>
                  <LabelWithMessage labelFor={EDesignModificationFormKeys.REVIEWER_COMMENT} text="Reviewer's Remarks" />
                  <FormTextareaWithMaxLength
                    formFieldMethods={formFieldMethods}
                    id={EDesignModificationFormKeys.REVIEWER_COMMENT}
                    max={800}
                    disabled={!isShowReviewerApproveBtn}
                  />
                </>
              );

              return (
                <>
                  {designModification?.status === EDesignModificationStatus.waitingForReviewerApproval &&
                    !isSuperseded && (
                      <Note
                        className="gcl-my-sm"
                        componentType={ComponentType.Info}
                        prefixIcon={<Icon iconName="Lightbulb" />}
                      >
                        This form is issued to the reviewer and it is waiting for approval
                      </Note>
                    )}

                  {isShowReviewerApproveBtn && !designModification?.reviewerProjectUserProfile && (
                    <LoadingButton
                      disabled={isAnyActionLoading}
                      prefixIconName="Pen"
                      type={ComponentType.Success}
                      isLoading={
                        mutationApprove.isLoading &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveReviewer
                      }
                      isSuccess={
                        mutationApprove.isSuccess &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveReviewer
                      }
                      isError={
                        mutationApprove.isError &&
                        confirmationActionType === EDesignModificationConfirmationActionType.approveReviewer
                      }
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                        onClickApprove(EDesignModificationConfirmationActionType.approveReviewer, e)
                      }
                      timeToResetSuccessState={3000}
                      timeToResetErrorState={5000}
                    >
                      Approve as Reviewer
                    </LoadingButton>
                  )}
                  {signatureComponent}
                  {commonForm}
                </>
              );
            }}
          />
        </Grid>
      )}

      {isShowReleasedConstructionInfo && (
        <Grid item xs={12} md={6}>
          <Card
            title="T3 Released for Construction"
            content={
              <>
                <LabelWithMessage
                  labelFor={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION}
                  text="Certificate"
                />
                <Attachments
                  className="gcl-mt-sm gcl-gems-t3-signature-card-body"
                  mode={isReadOnly || isAttachmentUploadLocked ? 'View' : mode}
                  id={EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION}
                  fileType={EProjectFileType.T3_ICE}
                  uploadFileRequestParam={{ designModificationId }}
                  useFetchFiles={useFetchAttachmentDesignModification}
                  useFetchFilesProps={{
                    projectId,
                    designModificationId: designModificationId ?? 0,
                    fileType: EProjectFileType.T3_ICE,
                  }}
                  useFetchFilesQueryKey={[
                    projectId,
                    designModificationId,
                    FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_MODIFICATIONS,
                    EProjectFileType.T3_ICE,
                  ]}
                />
              </>
            }
          />
        </Grid>
      )}
    </Grid>
  );
};
